import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { api } from "@/redux/api";
import { useAppDispatch } from "@/redux/hooks";
import { setFeedSelectedPerson } from "@/pages/Private/redux/feed/feed.slice";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { feedCardKey } from "./feedCardKey";
import { useFeedBulkRefresh } from "./useFeedBulkRefresh";

/** A lead the profiles list added to a card's list, as the API returned it (PREC-5041). */
export interface AddedFeedLead {
	listPersonId: number;
	prospectId: number | null;
}

/**
 * Owner of the Feed's add-lead interactions (PREC-5041 review S1/S3). It lives as
 * long as the Feed page — not as long as a card — so a Feed refetch that swaps the
 * cards for a spinner (Feed.tsx `renderCards`) cannot lose an add's answer, and a
 * menu closed and reopened cannot send the same add twice.
 */
export interface FeedPendingAdds {
	/**
	 * True while an add for this list/prospect is in flight, and after it succeeded until the
	 * Profiles payload stops offering that prospect (survives a menu reopen).
	 */
	isAddPending: (listId: number, prospectId: number) => boolean;
	/** Claims an interaction id; unique for the life of the owner, so a stale callback can be told apart. */
	nextInteractionId: () => number;
	/** Reserves the in-flight slot; false when this list/prospect is already being added. */
	beginAdd: (listId: number, prospectId: number) => boolean;
	/** Releases the slot and records the add's answer for the next refetch (no `added` = failure). */
	settleAdd: (args: {
		cardKey: number;
		listId: number;
		prospectId: number;
		added?: AddedFeedLead;
	}) => void;
	/** Add Person's bulk add: refresh now and keep refreshing through its processing window. */
	startBulkRefresh: () => void;
	/**
	 * Drops the settled-success guard for this list's prospects that the Profiles payload no
	 * longer offers as addable (the mutation's ListPerson invalidation refetched them as on-list).
	 */
	syncAddableProfiles: (listId: number, addableProspectIds: number[]) => void;
}

/** Feed-page instance of the owner; the card falls back to its own when rendered alone. */
export const FeedPendingAddsContext = createContext<FeedPendingAdds | null>(null);

export const useFeedPendingAddsContext = (): FeedPendingAdds | null =>
	useContext(FeedPendingAddsContext);

interface PendingAddEntry {
	cardKey: number;
	listId: number;
	listPersonId: number;
	prospectId: number | null;
	/** The card's people when the add settled; the answer is the first array that differs. */
	snapshot: FeedPerson[];
	/** The add resolved during a Feed fetch that predates it — wait for a fresh one (review S4). */
	awaitingFreshFetch: boolean;
}

interface UseFeedPendingAddsOptions {
	/** The page's cards (the Feed's own for the page owner, `[card]` for the lone card). */
	cards: FeedRunCard[];
	/** Lists currently selected; an add for a list the user left is dropped, never applied elsewhere. */
	selectedListIds: number[];
	isFeedFetching: boolean;
}

const pendingKey = (listId: number, prospectId: number) => `${listId}:${prospectId}`;
const entryKey = (cardKey: number, listId: number, prospectId: number) =>
	`${cardKey}:${listId}:${prospectId}`;

export const useFeedPendingAdds = ({
	cards,
	selectedListIds,
	isFeedFetching,
}: UseFeedPendingAddsOptions): FeedPendingAdds => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const startBulkRefresh = useFeedBulkRefresh();

	const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set());
	const pendingKeysRef = useRef<Set<string>>(new Set());
	// A succeeded add stays pending here until Profiles reports the person on the list, so a
	// reopened menu cannot send the same add again in the window before that refetch lands.
	const [settledKeys, setSettledKeys] = useState<ReadonlySet<string>>(() => new Set());
	const settledKeysRef = useRef<Set<string>>(new Set());
	const entriesRef = useRef<Map<string, PendingAddEntry>>(new Map());
	const interactionRef = useRef(0);

	const cardsRef = useRef(cards);
	const selectedListIdsRef = useRef(selectedListIds);
	const isFeedFetchingRef = useRef(isFeedFetching);

	useEffect(() => {
		cardsRef.current = cards;
		selectedListIdsRef.current = selectedListIds;
		isFeedFetchingRef.current = isFeedFetching;
	});

	const isAddPending = useCallback(
		(listId: number, prospectId: number) => {
			const key = pendingKey(listId, prospectId);

			return pendingKeys.has(key) || settledKeys.has(key);
		},
		[pendingKeys, settledKeys]
	);

	const nextInteractionId = useCallback(() => {
		interactionRef.current += 1;

		return interactionRef.current;
	}, []);

	const beginAdd = useCallback((listId: number, prospectId: number) => {
		const key = pendingKey(listId, prospectId);

		// The ref makes this hold before React re-renders, so a fast double click (or a
		// reopened menu) cannot slip a second request through (web §24c).
		if (pendingKeysRef.current.has(key) || settledKeysRef.current.has(key)) {
			return false;
		}

		pendingKeysRef.current.add(key);
		setPendingKeys(new Set(pendingKeysRef.current));

		return true;
	}, []);

	const settleAdd = useCallback<FeedPendingAdds["settleAdd"]>(
		({ cardKey, listId, prospectId, added }) => {
			const key = pendingKey(listId, prospectId);

			pendingKeysRef.current.delete(key);
			setPendingKeys(new Set(pendingKeysRef.current));

			if (!added) {
				return;
			}

			settledKeysRef.current.add(key);
			setSettledKeys(new Set(settledKeysRef.current));

			const card = cardsRef.current.find((currentCard) => feedCardKey(currentCard) === cardKey);

			entriesRef.current.set(entryKey(cardKey, listId, prospectId), {
				cardKey,
				listId,
				listPersonId: added.listPersonId,
				prospectId: added.prospectId,
				snapshot: card?.people ?? [],
				awaitingFreshFetch: isFeedFetchingRef.current,
			});

			// A fetch that was already running when the add resolved is not its answer.
			if (isFeedFetchingRef.current) {
				return;
			}

			dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));
		},
		[dispatch]
	);

	const syncAddableProfiles = useCallback<FeedPendingAdds["syncAddableProfiles"]>(
		(listId, addableProspectIds) => {
			const prefix = `${listId}:`;
			const addable = new Set(addableProspectIds);
			let changed = false;

			for (const key of settledKeysRef.current) {
				if (!key.startsWith(prefix) || addable.has(Number(key.slice(prefix.length)))) {
					continue;
				}

				settledKeysRef.current.delete(key);
				changed = true;
			}

			if (changed) {
				setSettledKeys(new Set(settledKeysRef.current));
			}
		},
		[]
	);

	useEffect(() => {
		const entries = entriesRef.current;

		if (entries.size === 0 || isFeedFetching) {
			return;
		}

		for (const [key, entry] of entries) {
			if (!selectedListIdsRef.current.includes(entry.listId)) {
				entries.delete(key);

				continue;
			}

			const card = cards.find((currentCard) => feedCardKey(currentCard) === entry.cardKey);

			// The card left the page (paging, another tab) — never answer on a different card.
			if (!card) {
				entries.delete(key);

				continue;
			}

			if (entry.awaitingFreshFetch) {
				// The stale fetch settled: stamp its result as seen, then ask for a fresh one.
				entry.awaitingFreshFetch = false;
				entry.snapshot = card.people;
				dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));

				continue;
			}

			if (card.people === entry.snapshot) {
				continue;
			}

			entries.delete(key);

			const selectedLead = card.people.find(
				(cardPerson) =>
					cardPerson.listPersonId === entry.listPersonId ||
					(entry.prospectId !== null && cardPerson.prospectId === entry.prospectId)
			);

			if (selectedLead) {
				dispatch(
					setFeedSelectedPerson({ runId: entry.cardKey, listPersonId: selectedLead.listPersonId })
				);

				continue;
			}

			// Added to the list, but the feed's score/link filters keep the lead off this card.
			showGlobalToast(t("feed.runCard.switchMenu.addedHiddenByFilters"), 6000, "success");
		}
	}, [cards, dispatch, isFeedFetching, t]);

	return useMemo(
		() => ({
			isAddPending,
			nextInteractionId,
			beginAdd,
			settleAdd,
			startBulkRefresh,
			syncAddableProfiles,
		}),
		[isAddPending, nextInteractionId, beginAdd, settleAdd, startBulkRefresh, syncAddableProfiles]
	);
};
